<template>
  <div class="card mb-3" :data-track-area="collapsible ? 'cart.shop-row' : null">
    <!-- Phones with 2+ shops (2026-10-30): each box folds to one line with its total, so the
         cart reads as "4 boxes = $246" instead of a long scroll; tap to open it. -->
    <button
      v-if="collapsible"
      type="button"
      :aria-expanded="open ? 'true' : 'false'"
      @click="open = !open"
      style="all:unset;box-sizing:border-box;width:100%;display:flex;align-items:center;gap:10px;padding:12px 14px;min-height:56px;cursor:pointer;"
    >
      <img
        v-if="group.sellerProfileImage"
        :src="(storageUrl || '').replace(/\/+$/, '') + '/' + group.sellerProfileImage"
        class="rounded-circle"
        width="34"
        height="34"
        alt=""
        style="flex-shrink:0;object-fit:cover;"
      />
      <span style="display:flex;flex-direction:column;min-width:0;flex-grow:1;">
        <strong style="font-size:.98rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ group.sellerDisplayName }}</strong>
        <span style="font-size:.8rem;color:#475467;font-weight:600;">{{ rowLine }}</span>
      </span>
      <strong style="font-size:1.02rem;white-space:nowrap;">${{ (group.subtotal + group.shippingTotal).toFixed(2) }}</strong>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475467" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" :style="{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none' }"><polyline points="6 9 12 15 18 9"></polyline></svg>
    </button>
    <template v-if="!collapsible || open">
    <div class="card-header bg-white py-3" :class="{ 'border-top': collapsible }">
      <div v-if="!collapsible" class="d-flex align-items-center">
        <img
          v-if="group.sellerProfileImage"
          :src="(storageUrl || '').replace(/\/+$/, '') + '/' + group.sellerProfileImage"
          class="rounded-circle mr-2"
          width="32"
          height="32"
          alt=""
        />
        <strong class="flex-grow-1" style="min-width:0;">
          <a :href="'/@' + group.sellerUserName" class="text-dark" style="text-decoration:none;">{{ group.sellerDisplayName }}</a>
        </strong>
        <span v-if="boxCount > 1" class="ml-2 text-nowrap" :style="boxChipStyle">{{ boxChip }}</span>
      </div>
      <div v-if="group.shippingNotice" class="mt-1">
        <small :class="group.shippingIsPaused ? 'text-warning font-weight-bold' : 'text-muted'">
          <i :data-feather="group.shippingIsPaused ? 'pause-circle' : 'calendar'" class="align-middle" style="width:14px;height:14px;"></i>
          {{ group.shippingNotice }}
        </small>
      </div>
      <div v-if="group.freeShippingThreshold && !group.qualifiesForFreeShipping" class="mt-2">
        <div class="progress" style="height: 6px;">
          <div
            class="progress-bar bg-success"
            :style="{ width: freeShippingProgress + '%' }"
          ></div>
        </div>
        <small class="text-muted">
          Add ${{ group.amountToFreeShipping.toFixed(2) }} more for free shipping
        </small>
      </div>
      <div v-if="group.qualifiesForFreeShipping" class="mt-1">
        <small class="text-success font-weight-bold">
          <i data-feather="check-circle" class="align-middle" style="width:14px;height:14px;"></i>
          Free shipping!
        </small>
      </div>
    </div>
    <div class="card-body p-0">
      <cart-item
        v-for="item in group.items"
        :key="item.cartItemId"
        :item="item"
        :storage-url="storageUrl"
        @remove="$emit('remove', item.cartItemId)"
        @update-quantity="(qty) => $emit('update-quantity', item.cartItemId, qty)"
        @update-shipping="(st) => $emit('update-shipping', item.cartItemId, st)"
      />
    </div>
    <!-- A similar item from another shop in the cart that saves this whole box (SecondShop). -->
    <div v-if="group.alternative" class="px-3 py-2 border-top d-flex align-items-center" style="background:#fff8e8;gap:10px;">
      <img
        v-if="group.alternative.option.imageFileName"
        :src="(storageUrl || '').replace(/\/+$/, '') + '/' + group.alternative.option.imageFileName"
        alt=""
        loading="lazy"
        style="width:48px;height:48px;object-fit:cover;border-radius:6px;flex-shrink:0;background:#f6f8fa;"
      />
      <div class="small" style="min-width:0;flex-grow:1;color:#5c3d00;line-height:1.35;">
        <strong style="color:#8a5a00;">Save ${{ group.alternative.saves.toFixed(2) }} shipping</strong>:
        get <a :href="'/listing/' + group.alternative.option.productId">{{ group.alternative.option.title }}</a>
        (${{ group.alternative.option.itemPrice.toFixed(2) }}) from {{ group.alternative.shopName }} instead, in the box you already have.
      </div>
      <button type="button" class="btn btn-sm btn-outline-primary text-nowrap" style="font-weight:800;min-height:36px;" @click="$emit('swap-alternative', group.alternative)">Swap</button>
    </div>
    <!-- "Add more from this shop": only listings that cost less shipping to add than to buy
         alone, with the real extra cost (SameShopNudge on the server). A row that scrolls. -->
    <div v-if="group.suggestions && group.suggestions.length" class="px-3 py-2 border-top" style="background:#f2f7ff;">
      <div class="small font-weight-bold mb-1">{{ suggestionsHeading }}</div>
      <div style="display:flex;gap:8px;overflow-x:auto;padding-bottom:4px;-webkit-overflow-scrolling:touch;">
        <div
          v-for="s in group.suggestions"
          :key="s.productId"
          style="flex:0 0 132px;background:#fff;border:1px solid #e1e6ef;border-radius:8px;overflow:hidden;display:flex;flex-direction:column;min-width:0;"
        >
          <a :href="'/listing/' + s.productId" class="text-dark" style="text-decoration:none;">
            <img
              v-if="s.imageFileName"
              :src="(storageUrl || '').replace(/\/+$/, '') + '/' + s.imageFileName"
              :alt="s.title"
              loading="lazy"
              style="width:100%;aspect-ratio:4/3;object-fit:cover;display:block;background:#f6f8fa;"
            />
            <div style="font-size:.72rem;font-weight:700;padding:5px 6px 0;line-height:1.2;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;min-height:2.4em;">{{ s.title }}</div>
          </a>
          <div style="font-size:.78rem;font-weight:800;padding:2px 6px 0;">+${{ s.itemPrice.toFixed(2) }}</div>
          <div style="font-size:.66rem;font-weight:700;padding:0 6px;" :style="{ color: s.extraShipping > 0 ? '#475467' : '#1a6b34' }">{{ s.shippingLabel }}</div>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary m-2 mt-auto"
            style="font-size:.74rem;font-weight:800;"
            @click="$emit('add-suggestion', group.sellerId, s.productId, s.shippingTypeId)"
          >Add</button>
        </div>
      </div>
    </div>
    <div class="px-3 pt-3 pb-2 border-top">
      <label :for="'seller-note-' + group.sellerId" class="small text-muted mb-1">
        Note for {{ group.sellerDisplayName }} <span class="text-muted">(optional)</span>
      </label>
      <textarea
        :id="'seller-note-' + group.sellerId"
        v-model="note"
        @input="onInput"
        class="form-control form-control-sm"
        rows="2"
        maxlength="1000"
        placeholder="e.g. delivery preferences, packaging requests, gift, special instructions"
      ></textarea>
      <div class="d-flex justify-content-between align-items-center mt-1">
        <small class="text-muted">
          <span v-if="saveStatus === 'saving'"><i data-feather="loader" style="width:12px;height:12px;"></i> Saving…</span>
          <span v-else-if="saveStatus === 'saved'" class="text-success">Saved</span>
          <span v-else>&nbsp;</span>
        </small>
        <small class="text-muted">{{ note.length }}/1000</small>
      </div>
    </div>
    <!-- Items, then shipping with how it adds up for this shop (CartShippingSummary). -->
    <div class="card-footer bg-white">
      <div class="d-flex justify-content-between">
        <span class="text-muted">Items</span>
        <strong>${{ group.subtotal.toFixed(2) }}</strong>
      </div>
      <div
        class="d-flex justify-content-between align-items-start mt-2 p-2 rounded"
        :style="shippingRowStyle"
      >
        <div style="min-width:0;">
          <strong>Shipping</strong>
          <div v-if="group.shippingSummary" class="small" :style="{ color: shippingTextColor }">{{ group.shippingSummary }}</div>
        </div>
        <strong class="ml-2 text-nowrap">${{ group.shippingTotal.toFixed(2) }}</strong>
      </div>
      <button v-if="collapsible" type="button" class="btn btn-link p-0 mt-2" style="color:#b42318;font-weight:700;font-size:.85rem;min-height:36px;" @click="$emit('remove-shop', group)">Remove this shop</button>
    </div>
    </template>
  </div>
</template>

<script>
import CartItem from './CartItem.vue';

export default {
  components: { CartItem },
  props: {
    group: Object,
    storageUrl: String,
    initialNote: { type: String, default: '' },
    boxNumber: { type: Number, default: 1 },
    boxCount: { type: Number, default: 1 },
    isAuthenticated: { type: Boolean, default: false },
    collapsible: { type: Boolean, default: false },
    startOpen: { type: Boolean, default: false }
  },
  data() {
    return {
      note: this.initialNote || '',
      saveStatus: '',
      saveTimer: null,
      open: this.startOpen
    };
  },
  watch: {
    initialNote(val) {
      if (val !== this.note && !this.saveTimer) this.note = val || '';
    }
  },
  computed: {
    // "2 items · $63 + $20 shipping" for the folded row.
    rowLine() {
      var n = (this.group.items || []).reduce(function (sum, i) { return sum + (i.quantity || 1); }, 0);
      var line = n + (n === 1 ? ' item · $' : ' items · $') + this.group.subtotal.toFixed(2);
      return this.group.shippingTotal > 0 ? line + ' + $' + this.group.shippingTotal.toFixed(2) + ' shipping' : line + ' · free shipping';
    },
    // "Box 1", then "Box 2 · +$9.00" for each box after the first (the second-shop charge).
    boxChip() {
      return this.boxNumber > 1 && this.group.shippingTotal > 0
        ? 'Box ' + this.boxNumber + ' · +$' + this.group.shippingTotal.toFixed(2)
        : 'Box ' + this.boxNumber;
    },
    boxChipStyle() {
      var extra = this.boxNumber > 1 && this.group.shippingTotal > 0;
      return { fontSize: '.75rem', fontWeight: 700, borderRadius: '10px', padding: '2px 8px',
               color: extra ? '#8a5a00' : '#4a5465', background: extra ? '#fff6e0' : '#f1f3f7' };
    },
    suggestionsHeading() {
      var allFree = (this.group.suggestions || []).every(function (s) { return s.extraShipping === 0; });
      return allFree ? 'Add more from this shop, no extra shipping' : 'Add more from this shop, shipping combined';
    },
    // Green when the shop combines shipping (one box / included / free), amber when every
    // item pays full shipping, plain otherwise.
    shippingRowStyle() {
      var t = this.group.shippingSummaryTone;
      if (t === 'good') return { background: '#e7f7eb' };
      if (t === 'costly') return { background: '#fff6e0' };
      return { background: '#f7f9fc' };
    },
    shippingTextColor() {
      var t = this.group.shippingSummaryTone;
      return t === 'good' ? '#1a6b34' : t === 'costly' ? '#8a5a00' : '#6c757d';
    },
    freeShippingProgress() {
      if (!this.group.freeShippingThreshold) return 0;
      var pct = (this.group.subtotal / this.group.freeShippingThreshold) * 100;
      return Math.min(pct, 100);
    }
  },
  methods: {
    onInput() {
      this.saveStatus = '';
      if (this.saveTimer) clearTimeout(this.saveTimer);
      this.saveTimer = setTimeout(this.save, 600);
    },
    save() {
      this.saveStatus = 'saving';
      var self = this;
      if (!this.isAuthenticated) {
        // Guest: persist to localStorage; checkout page will ship these along with the cart
        try {
          var raw = localStorage.getItem('lightfish-guest-seller-notes');
          var map = raw ? JSON.parse(raw) : {};
          if (!map || typeof map !== 'object') map = {};
          if (this.note && this.note.trim()) map[this.group.sellerId] = this.note;
          else delete map[this.group.sellerId];
          localStorage.setItem('lightfish-guest-seller-notes', JSON.stringify(map));
          this.saveStatus = 'saved';
          this.saveTimer = null;
          setTimeout(function() { if (self.saveStatus === 'saved') self.saveStatus = ''; }, 2000);
        } catch (e) {
          this.saveStatus = '';
          this.saveTimer = null;
        }
        return;
      }
      var token = (document.querySelector('input[name="__RequestVerificationToken"]') || {}).value || '';
      fetch('/cart/seller-note', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'RequestVerificationToken': token },
        body: JSON.stringify({ sellerId: this.group.sellerId, note: this.note })
      }).then(function(r) {
        self.saveStatus = r.ok ? 'saved' : '';
        self.saveTimer = null;
        if (r.ok) setTimeout(function() { if (self.saveStatus === 'saved') self.saveStatus = ''; }, 2000);
      }).catch(function() {
        self.saveStatus = '';
        self.saveTimer = null;
      });
    }
  }
};
</script>
